import React, { useRef, useEffect, useState } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import { MotionDiv } from '@/utils/optimizedMotion';

interface ParallaxSectionProps {
  variant?: 'top' | 'middle' | 'bottom';
  className?: string;
  children: React.ReactNode;
  reduceMotion?: boolean;
}

/**
 * ParallaxSection Component
 * 
 * A performance-optimized component that adds subtle parallax effects to sections.
 * Includes conditional rendering for mobile devices and respects reduced motion preferences.
 */
const ParallaxSection: React.FC<ParallaxSectionProps> = ({
  variant = 'top',
  className = '',
  children,
  reduceMotion = false
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if device is mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  // Simplified parallax values - less movement for better performance
  const yOffset = variant === 'top' ? -30 : variant === 'middle' ? -20 : -10;
  
  // Only apply parallax if not mobile and motion is not reduced
  const y = !isMobile && !reduceMotion 
    ? useTransform(scrollYProgress, [0, 1], [0, yOffset])
    : useTransform(scrollYProgress, [0, 1], [0, 0]); // No movement if mobile or reduced motion
  
  // Fade effect is more subtle and works well on all devices
  const opacity = useTransform(
    scrollYProgress, 
    [0, 0.2, 0.8, 1], 
    [1, 1, 0.9, 0.8]
  );
  
  // Decorative elements based on position variant
  const renderDecorativeElements = () => {
    // Skip decorative elements on mobile for better performance
    if (isMobile) return null;
    
    if (variant === 'top') {
      return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-purple-200 dark:bg-purple-900/30 blur-3xl opacity-20"></div>
          <div className="absolute bottom-20 -left-20 w-72 h-72 rounded-full bg-purple-100 dark:bg-purple-900/20 blur-3xl opacity-10"></div>
        </div>
      );
    } 
    
    if (variant === 'middle') {
      return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute top-20 -left-20 w-72 h-72 rounded-full bg-purple-200 dark:bg-purple-900/30 blur-3xl opacity-20"></div>
        </div>
      );
    }
    
    if (variant === 'bottom') {
      return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-purple-100 dark:bg-purple-900/20 blur-3xl opacity-15"></div>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <motion.section
      ref={sectionRef}
      className={`relative ${className}`}
      style={{ y, opacity }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {renderDecorativeElements()}
      <div className="relative z-10">
        {children}
      </div>
    </motion.section>
  );
};

export default ParallaxSection;
